<template>
  <div :class="fullview ? 'message-box-overlay' : ''">
    <div :class="`message-box ${handleView()}`">
      <div
        :class="`message-box-header ${
          type ? 'message-box-header-' + type : ''
        }`"
      >
        <div class="message-box-header-title">
          {{ truncatedTitle }}
        </div>
        <div class="message-box-header-controls">
          <NrIconButton
            variant="bordered-neutral"
            size="small"
            :icon="{ name: 'minus', type: 'regular' }"
            aria-label="Minimize"
            @click="handleMinimize"
          />
          <NrIconButton
            v-if="!fullview"
            aria-label="Expand"
            variant="bordered-neutral"
            :icon="{ name: 'expand-arrows', type: 'regular' }"
            size="small"
            @click="handleExpand"
          />
          <NrIconButton
            aria-label="Maximize"
            v-if="fullview"
            variant="bordered-neutral"
            :icon="{
              name: 'compress-alt',
              type: 'regular'
            }"
            size="small"
            @click="handleUnexpand"
          />
          <NrIconButton
            aria-label="Close"
            variant="bordered-neutral"
            :icon="{ name: 'times', type: 'regular' }"
            size="small"
            @click="$emit('on-close')"
          />
        </div>
      </div>

      <div class="message-box-content" v-show="!minimized">
        <slot />
      </div>

      <div class="message-box-controls" v-show="!minimized">
        <NrButton
          @click="$emit('on-cancel')"
          variant="bordered-primary"
          label="Cancel"
          class="m-right-8"
        >
          Cancel
        </NrButton>
        <NrTooltip v-if="submitTooltipText" :label="submitTooltipText">
          <NrButton @click="$emit('on-submit')" :disabled="disableSubmit">
            {{ submitText }}
          </NrButton>
        </NrTooltip>
        <NrButton v-else @click="$emit('on-submit')" :disabled="disableSubmit">
          {{ submitText }}
        </NrButton>
      </div>
    </div>
  </div>
</template>

<script>
import NrButton from '../NrButton/nr-button.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'
import NrTooltip from '../NrTooltip/NrTooltip.vue'
import truncate from 'lodash/truncate'

export default {
  name: 'MessageBox',
  components: { NrButton, NrIconButton, NrTooltip },
  props: {
    title: {
      type: String,
      default: ''
    },
    submitText: {
      type: String,
      default: 'Submit'
    },
    type: {
      type: String,
      default: 'default'
    },
    disableSubmit: {
      type: Boolean,
      default: false
    },
    submitTooltipText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      minimized: false,
      expanded: false,
      fullview: false,
      currentState: 'expanded'
    }
  },
  computed: {
    truncatedTitle() {
      const length = this.minimized ? 20 : 50
      return truncate(this.title, { length })
    }
  },
  methods: {
    handleMinimize() {
      if (this.currentState === 'minimized') {
        this.handleExpand()
        return
      }
      this.minimized = true
      this.expanded = false
      this.fullview = false
      this.currentState = 'minimized'
    },
    handleExpand() {
      if (this.minimized) {
        this.fullview = false
        this.minimized = false
        this.expanded = true
        this.currentState = 'expanded'
      } else {
        this.fullview = true
        this.minimized = false
        this.expanded = false
        this.currentState = 'fullview'
      }
    },
    handleUnexpand() {
      this.fullview = false
      this.minimized = false
      this.expanded = true
    },
    handleView() {
      if (this.minimized) {
        return 'message-box-container-minimized'
      } else if (this.fullview) {
        return 'message-box-container-fullview'
      } else if (this.expanded) {
        return 'message-box-container'
      } else {
        return 'message-box-container'
      }
    }
  }
}
</script>

<style lang="scss">
@import '../../styles/tokens/tokens.scss';

.message-box-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background-color: rgba(9, 9, 11, 0.86);
}
.message-box {
  box-shadow:
    0 4px 8px 0 rgba(0, 0, 0, 0.2),
    0 6px 20px 0 rgba(0, 0, 0, 0.19);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  &-container {
    background: $color-white;
    position: fixed;
    width: 640px;
    right: 0;
    bottom: 0;
    z-index: 50;
  }
  &-container-minimized {
    position: fixed;
    height: 54px;
    width: 344px;
    right: 0;
    bottom: 0;
  }
  &-container-fullview {
    background: $color-white;
    position: absolute;
    height: 90%;
    width: 80%;
    margin: 5% 10%;
    left: 0;
    top: 0;
  }
  &-header {
    display: flex;
    height: 54px;
    padding: $spacing-12;
    justify-content: space-between;
    &-default {
      background: black;
      color: $color-white;
    }
    &-green {
      background: $color-green-100;
      color: $color-white;
    }
    &-orange {
      background: $color-orange-100;
      color: black;
    }
    &-title {
      font-weight: $font-weight-semibold;
      font-size: $font-size-4;
    }
  }
  &-content {
    padding: $spacing-12 $spacing-12 0 $spacing-12;
    min-height: calc(100% - 54px - 72px);
  }
  &-controls {
    padding: $spacing-12;
    text-align: right;
  }
}
@media only screen and (max-width: 600px) {
  .message-box {
    &-container {
      background: $color-white;
      position: fixed;
      width: 100%;
    }

    &-header-title {
      width: 250px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &-header-controls {
      min-width: 155px;
      text-align: right;
    }
  }
}
</style>
